import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { productsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { getStockState, getRemainingToAdd, isRemainingLow } from '../utils/stock';
import './Products.css';

interface Category {
  id: number;
  name: string;
  order_requirements: string;
  available_until: string;
  show_order_banner: boolean;
  show_availability_banner: boolean;
  delivery_window: string;
  show_delivery_banner: boolean;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<{[key: number]: number}>({});
  const [addingToCart, setAddingToCart] = useState<number | null>(null);
  const { addItem, getTotalItems, items: cartItems } = useCart();
  const navigate = useNavigate();

  // Units of this product already sitting in the cart, so the page never offers
  // more than the remaining stock across repeated additions.
  const quantityInCart = (productId: number) =>
    cartItems.find(item => item.productId === productId)?.quantity ?? 0;

  // How many more units may be added: null means unlimited (tracking off).
  const remainingToAdd = (product: Product) =>
    getRemainingToAdd(product, quantityInCart(product.id));
  
  const getCategoryDisplayName = (categoryName: string) => {
    // Find the category in our categories list to get the proper display name
    const category = categories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());
    if (category) {
      // If it's the 'Mums' category, display as 'Hardy Mums'
      return category.name.toLowerCase() === 'mums' ? 'Hardy Mums' : category.name;
    }
    // Fallback for categories not in our list
    return categoryName.toLowerCase() === 'mums' ? 'Hardy Mums' : categoryName;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productsResponse = await productsAPI.getAll();
        setProducts(productsResponse.data);
        
        // Try to fetch categories, fallback to hardcoded if fails
        try {
          const categoriesResponse = await fetch('/api/categories');
          if (categoriesResponse.ok) {
            const categoriesData = await categoriesResponse.json();
            setCategories(categoriesData);
          } else {
            throw new Error('Categories API failed');
          }
        } catch (categoryError) {
          console.warn('Using fallback categories:', categoryError);
          // Fallback categories
          setCategories([
            { id: 1, name: 'Mums', order_requirements: 'Minimum 4 hardy mums (any color combination)', available_until: '', show_order_banner: true, show_availability_banner: false, delivery_window: '', show_delivery_banner: false },
            { id: 2, name: 'Pumpkins', order_requirements: '', available_until: 'October 31st', show_order_banner: false, show_availability_banner: true, delivery_window: '', show_delivery_banner: false },
            { id: 3, name: 'Tropical Plants', order_requirements: '', available_until: '', show_order_banner: false, show_availability_banner: false, delivery_window: '', show_delivery_banner: false },
            { id: 4, name: 'Produce Boxes', order_requirements: '', available_until: '', show_order_banner: false, show_availability_banner: false, delivery_window: '', show_delivery_banner: false },
            { id: 5, name: 'Other', order_requirements: '', available_until: '', show_order_banner: false, show_availability_banner: false, delivery_window: '', show_delivery_banner: false }
          ]);
        }
        
        // Initialize quantities
        const initialQuantities: {[key: number]: number} = {};
        productsResponse.data.forEach((product: Product) => {
          initialQuantities[product.id] = 1;
        });
        setQuantities(initialQuantities);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Set fallback categories even on complete failure
        setCategories([
          { id: 1, name: 'Mums', order_requirements: 'Minimum 4 hardy mums (any color combination)', available_until: '', show_order_banner: true, show_availability_banner: false, delivery_window: '', show_delivery_banner: false },
          { id: 2, name: 'Pumpkins', order_requirements: '', available_until: 'October 31st', show_order_banner: false, show_availability_banner: true, delivery_window: '', show_delivery_banner: false },
          { id: 3, name: 'Tropical Plants', order_requirements: '', available_until: '', show_order_banner: false, show_availability_banner: false, delivery_window: '', show_delivery_banner: false },
          { id: 4, name: 'Produce Boxes', order_requirements: '', available_until: '', show_order_banner: false, show_availability_banner: false, delivery_window: '', show_delivery_banner: false },
          { id: 5, name: 'Other', order_requirements: '', available_until: '', show_order_banner: false, show_availability_banner: false, delivery_window: '', show_delivery_banner: false }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddToCart = (product: Product) => {
    // Clamp again at the moment of adding, in case stock shrank while the page
    // was open. The order endpoint is the real enforcement point; this just
    // avoids sending a request that is certain to be rejected.
    const quantity = clampToStock(product, quantities[product.id] || 1);
    if (quantity < 1) return;

    setAddingToCart(product.id);

    addItem({
      productId: product.id,
      name: product.name,
      price: parseFloat(product.price),
      quantity,
      category: product.category
    });

    // Reset the picker, since the remaining allowance has just gone down.
    setQuantities(prev => ({ ...prev, [product.id]: 1 }));

    setTimeout(() => {
      setAddingToCart(null);
    }, 1000);
  };

  // Clamps to whatever is still available so the selector can never propose a
  // quantity the order endpoint would reject.
  const clampToStock = (product: Product, quantity: number) => {
    const remaining = remainingToAdd(product);
    const upperBound = remaining === null ? Number.MAX_SAFE_INTEGER : remaining;
    return Math.max(1, Math.min(quantity, Math.max(1, upperBound)));
  };

  const updateQuantity = (product: Product, change: number) => {
    setQuantities(prev => ({
      ...prev,
      [product.id]: clampToStock(product, (prev[product.id] || 1) + change)
    }));
  };

  const setQuantity = (product: Product, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [product.id]: clampToStock(product, quantity)
    }));
  };

  const getProductsByCategory = (categoryName: string) => {
    return products.filter(product => {
      // Handle both 'Mums' and 'Hardy Mums' category names
      const productCategory = product.category.toLowerCase();
      const searchCategory = categoryName.toLowerCase();
      
      // If searching for 'mums', match both 'mums' and 'hardy mums'
      if (searchCategory === 'mums') {
        return (productCategory === 'mums' || productCategory === 'hardy mums') && product.is_active;
      }
      
      // If searching for 'hardy mums', match both 'mums' and 'hardy mums'
      if (searchCategory === 'hardy mums') {
        return (productCategory === 'mums' || productCategory === 'hardy mums') && product.is_active;
      }
      
      // For other categories, exact match
      return productCategory === searchCategory && product.is_active;
    });
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="products">
      <div className="products-header">
        <h1>Our Products</h1>
        <p>Fresh, seasonal plants and produce delivered to your door!</p>
        {getTotalItems() > 0 && (
          <button 
            className="checkout-btn-products"
            onClick={() => navigate('/cart')}
          >
            🛒 Checkout ({getTotalItems()} items)
          </button>
        )}
      </div>

      {categories.map(category => {
        const categoryProducts = getProductsByCategory(category.name);
        if (categoryProducts.length === 0) return null;

        return (
          <div key={category.id} className="category-section">
            <h2 className="category-title">{getCategoryDisplayName(category.name)}</h2>
            {category.show_order_banner && category.order_requirements && (
              <div className="order-info-banner">
                <div className="banner-content">
                  <div className="banner-icon">ℹ️</div>
                  <div className="banner-text">
                    <strong>Order Requirements:</strong> {category.order_requirements}
                  </div>
                </div>
              </div>
            )}
            {category.show_availability_banner && category.available_until && (
              <div className="availability-banner">
                <div className="banner-content">
                  <div className="banner-icon">🕒</div>
                  <div className="banner-text">
                    <strong>Available Until:</strong> {category.available_until}
                  </div>
                </div>
              </div>
            )}
            {category.show_delivery_banner && category.delivery_window && (
              <div className="delivery-banner">
                <div className="banner-content">
                  <div className="banner-icon">🚚</div>
                  <div className="banner-text">
                    <strong>Delivered Between:</strong> {category.delivery_window}
                  </div>
                </div>
              </div>
            )}
            <div className="products-grid">
              {categoryProducts.map(product => {
                const stockState = getStockState(product);
                const isOutOfStock = stockState === 'out';
                const remaining = remainingToAdd(product);
                const inCart = quantityInCart(product.id);
                // Stock exists but it is all already in this customer's cart.
                const allInCart = remaining === 0 && !isOutOfStock;
                const selectedQuantity = quantities[product.id] || 1;
                // Cap the dropdown at 20 as before, or at what is left if lower.
                const selectableMax = remaining === null ? 20 : Math.min(20, remaining);

                return (
                <div
                  key={product.id}
                  className={`product-card ${isOutOfStock ? 'out-of-stock' : ''}`}
                >
                  {product.image_url && (
                    <div className="product-image">
                      <img src={product.image_url} alt={product.name} />
                      {isOutOfStock && <div className="stock-overlay">OUT OF STOCK</div>}
                    </div>
                  )}
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-description">{product.description}</p>
                    <div className="product-price">${parseFloat(product.price).toFixed(2)}</div>

                    {stockState === 'low' && (
                      <div className="stock-badge low">
                        Only {product.inventory_count} left
                      </div>
                    )}
                    {isOutOfStock && !product.image_url && (
                      <div className="stock-badge out">OUT OF STOCK</div>
                    )}

                    {!product.is_active ? (
                      <div className="unavailable">Currently Unavailable</div>
                    ) : isOutOfStock ? (
                      <div className="unavailable out">
                        Sold out for this season
                      </div>
                    ) : allInCart ? (
                      <div className="unavailable in-cart">
                        All {inCart} available in your cart
                      </div>
                    ) : (
                      <div className="cart-controls">
                        <div className="quantity-controls">
                          <button 
                            className="quantity-btn"
                            onClick={() => updateQuantity(product, -1)}
                            disabled={selectedQuantity <= 1}
                            aria-label={`Decrease ${product.name} quantity`}
                          >
                            -
                          </button>
                          <span className="quantity-display">{selectedQuantity}</span>
                          <button 
                            className="quantity-btn"
                            onClick={() => updateQuantity(product, 1)}
                            disabled={remaining !== null && selectedQuantity >= remaining}
                            aria-label={`Increase ${product.name} quantity`}
                          >
                            +
                          </button>
                          <select 
                            className="quantity-select"
                            value={Math.min(selectedQuantity, selectableMax)}
                            onChange={(e) => setQuantity(product, parseInt(e.target.value))}
                            aria-label={`${product.name} quantity`}
                          >
                            {[...Array(selectableMax)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                          </select>
                        </div>
                        {inCart > 0 && (
                          isRemainingLow(product, remaining) ? (
                            // Only surface the running count when stock is
                            // actually getting tight enough to matter.
                            <p className="stock-note tight">
                              {inCart} in your cart, {remaining} still available
                            </p>
                          ) : (
                            <p className="stock-note">
                              {inCart} added to your cart
                            </p>
                          )
                        )}
                        <button 
                          className={`add-to-cart-btn ${addingToCart === product.id ? 'adding' : ''}`}
                          onClick={() => handleAddToCart(product)}
                          disabled={addingToCart === product.id}
                        >
                          {addingToCart === product.id ? '✓ Added!' : 'Add to Cart'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );
      })}
      
      {products.filter(p => p.is_active).length === 0 && (
        <div className="no-products">
          <h2>No products available at this time</h2>
          <p>Please check back later for new arrivals!</p>
        </div>
      )}
    </div>
  );
};

export default Products;